import Loader from "@components/Loader";
import { useEffect, useState } from "react";

const BRL_FMT = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const DATE_FMT = new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" });
const fmtBRL = (v) => BRL_FMT.format(v || 0);
const fmtDate = (d) => (d ? DATE_FMT.format(new Date(d)) : "—");
const toInputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const datesEqualUTC = (a, b) => !!a && !!b && new Date(a).getTime() === new Date(b).getTime();

const stageState = (s, today) => {
  if (s.data_efetiva) return "done";
  if (s.data_limite && new Date(s.data_limite) < today) return "late";
  return "pending";
};

const SupplyItemDetailModal = ({ item, stageMeta, templates = [], buildingId, canAssertSiengeAbsence = false, getSupplyItemDetail, onReprogram, onToggleLock, onIgnoreSuggestion, onSetCycle, onCreateCycle, onEditCycles, onConfirmStage }) => {
  const [rows, setRows] = useState(null);
  const [siengeDetail, setSiengeDetail] = useState(null);
  const [confirmingStage, setConfirmingStage] = useState(null);
  const [needInput, setNeedInput] = useState(toInputDate(item.need_date));
  const [confirmingAuto, setConfirmingAuto] = useState(false);
  const [pendingCycle, setPendingCycle] = useState(null);
  const [creatingCycle, setCreatingCycle] = useState(false);
  const [newCycle, setNewCycle] = useState({ name: "", baseId: "" });
  const [savingCycle, setSavingCycle] = useState(false);
  const today = new Date();

  useEffect(() => {
    setConfirmingAuto(false);
    setPendingCycle(null);
    setCreatingCycle(false);
    (async () => {
      try {
        const res = await getSupplyItemDetail(buildingId, item.resource_id);
        setRows(res?.rows ?? []);
        setSiengeDetail(res?.sienge_detail ?? null);
      } catch {
        setRows([]);
        setSiengeDetail(null);
      }
    })();
  }, [item.resource_id]);

  const currentCycleId = item.template_id || templates.find((t) => t.is_default)?._id || "";
  // Etapas concluídas cuja key não existe no ciclo alvo — serão reiniciadas na troca.
  const lostStages = (target) =>
    (item.stages || []).filter((s) => s.data_efetiva && !(target?.stages || []).some((ts) => ts.key === s.key));

  const handleCycleSelect = (value) => {
    if (value === "__create__") {
      setNewCycle({ name: "", baseId: currentCycleId });
      setCreatingCycle(true);
      return;
    }
    if (value === currentCycleId) return;
    const target = templates.find((t) => t._id === value);
    if (!target) return;
    if (lostStages(target).length > 0) setPendingCycle(target);
    else runSetCycle(target._id);
  };

  // Guarded (single-flight) cycle switch: prevents a double-click from firing a
  // second request whose response (e.g. a "same cycle" no-op) races into a false
  // error banner while the first assignment already persisted.
  const runSetCycle = async (templateId) => {
    if (savingCycle) return;
    setSavingCycle(true);
    try {
      await onSetCycle(templateId);
    } finally {
      setSavingCycle(false);
    }
  };

  const handleCreateCycle = async () => {
    if (savingCycle) return;
    setSavingCycle(true);
    try {
      const created = await onCreateCycle(newCycle.name.trim(), newCycle.baseId || currentCycleId);
      if (created) {
        setCreatingCycle(false);
        setNewCycle({ name: "", baseId: "" });
        await onSetCycle(created._id);
      }
    } finally {
      setSavingCycle(false);
    }
  };

  useEffect(() => { setNeedInput(toInputDate(item.need_date)); }, [item.need_date]);

  const baselineDiffers = (s) =>
    s.data_limite_baseline && s.data_limite &&
    new Date(s.data_limite_baseline).getTime() !== new Date(s.data_limite).getTime();

  return (
    <div className="supply-detail">
      <header className="supply-detail-header">
        <h2 title={item.resource_description}>{item.resource_description}</h2>
        <span className="supply-detail-meta">
          {item.measurement_unit} · {fmtBRL(item.evaluated_total)} · Curva {item.curve_class} ·
          <span className={`status-pill status-${item.status}`}>{item.status}</span>
        </span>
      </header>

      {templates.length > 0 && onSetCycle && (
        <section className="supply-detail-cycle" style={{ marginBottom: 12 }}>
          <div className="anchor-head">
            <label>Ciclo de compras</label>
            {onEditCycles && (
              <button type="button" className="link-button" onClick={onEditCycles}>
                Editar ciclos nas Configurações
              </button>
            )}
          </div>
          {creatingCycle ? (
            <div className="flex gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="default-input"
                style={{ width: "200px", marginBottom: 0 }}
                placeholder="Nome do novo ciclo (ex.: Elevador)"
                value={newCycle.name}
                onChange={(e) => setNewCycle((p) => ({ ...p, name: e.target.value }))}
              />
              <select style={{ marginBottom: 0 }} value={newCycle.baseId} onChange={(e) => setNewCycle((p) => ({ ...p, baseId: e.target.value }))}>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>Copiar etapas de: {t.name}</option>
                ))}
              </select>
              <button className="btn-primary" disabled={!newCycle.name.trim() || savingCycle} onClick={handleCreateCycle}>{savingCycle ? "Criando…" : "Criar e usar"}</button>
              <button className="btn-ghost" disabled={savingCycle} onClick={() => setCreatingCycle(false)}>Cancelar</button>
            </div>
          ) : pendingCycle ? (
            <div className="anchor-confirm">
              <span>
                {lostStages(pendingCycle).length} etapa(s) concluída(s) sem correspondência no ciclo "{pendingCycle.name}" serão reiniciadas. As etapas com a mesma key mantêm o que já foi feito.
              </span>
              <div className="anchor-confirm-actions">
                <button className="btn-primary" disabled={savingCycle} onClick={() => { const id = pendingCycle._id; setPendingCycle(null); runSetCycle(id); }}>{savingCycle ? "Trocando…" : "Confirmar troca"}</button>
                <button className="btn-ghost" disabled={savingCycle} onClick={() => setPendingCycle(null)}>Cancelar</button>
              </div>
            </div>
          ) : (
            <select style={{ marginBottom: 0 }} value={currentCycleId} onChange={(e) => handleCycleSelect(e.target.value)}>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>{t.name}{t.is_default ? " (padrão)" : ""}</option>
              ))}
              <option value="__create__">+ Criar novo ciclo…</option>
            </select>
          )}
        </section>
      )}

      <section className="supply-detail-anchor">
        <div className="anchor-head">
          <label>Necessidade do insumo na obra (âncora)</label>
          {item.need_date_suggested && (
            <span className={`mode-pill ${datesEqualUTC(item.need_date, item.need_date_suggested) ? "mode-auto" : "mode-manual"}`}>
              {datesEqualUTC(item.need_date, item.need_date_suggested) ? "Automático" : "Manual"}
            </span>
          )}
        </div>

        {onReprogram ? <div className="anchor-row">
          <input type="date" value={needInput} onChange={(e) => setNeedInput(e.target.value)} />
          <button className="btn-primary" disabled={!needInput || needInput === toInputDate(item.need_date)}
            onClick={() => onReprogram(needInput, "manual")}>Reprogramar</button>
        </div> : <span className="anchor-baseline">Data atual: {fmtDate(item.need_date)}</span>}

        {!item.need_date_suggested ? (
          <span className="anchor-baseline">Sem data no cronograma.</span>
        ) : datesEqualUTC(item.need_date, item.need_date_suggested) ? (
          <span className="anchor-auto-ok">✓ Seguindo o cronograma.</span>
        ) : (
          <div className="anchor-auto">
            <span className="anchor-auto-info">ⓘ O cronograma indica {fmtDate(item.need_date_suggested)}.</span>
            {onReprogram && (confirmingAuto ? (
              <div className="anchor-confirm">
                <span>Cancela a data manual ({fmtDate(item.need_date)}) e segue o cronograma ({fmtDate(item.need_date_suggested)}).</span>
                <div className="anchor-confirm-actions">
                  <button className="btn-primary"
                    onClick={() => { setConfirmingAuto(false); onReprogram(toInputDate(item.need_date_suggested), "suggestion"); }}>Confirmar</button>
                  <button className="btn-ghost" onClick={() => setConfirmingAuto(false)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <button className="btn-ghost" onClick={() => setConfirmingAuto(true)}>
                Voltar pro automático ({fmtDate(item.need_date_suggested)})
              </button>
            ))}
          </div>
        )}

        <span className="anchor-baseline">Estratégia inicial: {fmtDate(item.need_date_baseline)}</span>
      </section>

      <section className="supply-detail-timeline">
        {stageMeta.map((meta, idx) => {
          const s = item.stages.find((x) => x.key === meta.key) || {};
          const state = stageState(s, today);
          // O lead_time da última etapa (âncora "Necessidade do insumo na obra") é
          // ignorado por convenção — é o marco final, não tem prazo até uma próxima.
          const isAnchor = idx === stageMeta.length - 1;
          const showLead = !isAnchor && Number.isFinite(meta.lead_time_days);
          const suggestion = !s.data_efetiva ? item.sienge?.suggestions?.[meta.key] : null;
          return (
            <div key={meta.key} className={`timeline-row state-${state}`}>
              <span className="timeline-name">
                {meta.name}
                {showLead && <span className="timeline-lead" title="Prazo cadastrado desta etapa">{meta.lead_time_days} {meta.lead_time_days === 1 ? "dia" : "dias"}</span>}
              </span>
              <span className="timeline-dates">
                <span className="t-limite">limite: {fmtDate(s.data_limite)}</span>
                {baselineDiffers(s) && <span className="t-baseline">(inicial: {fmtDate(s.data_limite_baseline)})</span>}
                {s.data_efetiva && <span className="t-efetiva">efetiva: {fmtDate(s.data_efetiva)}</span>}
                {suggestion && onConfirmStage && (
                  confirmingStage === meta.key ? (
                    <span className="t-sienge-confirm">
                      <button className="btn-primary" onClick={() => { setConfirmingStage(null); onConfirmStage(meta.key, suggestion); }}>Confirmar {fmtDate(suggestion)}</button>
                      <button className="btn-ghost" onClick={() => setConfirmingStage(null)}>Cancelar</button>
                    </span>
                  ) : (
                    <button
                      className="btn-ghost t-sienge-suggestion"
                      title="Data detectada nos documentos do Sienge — confirme para concluir a etapa"
                      onClick={() => setConfirmingStage(meta.key)}
                    >
                      Sienge indica {fmtDate(suggestion)} ✓ Confirmar
                    </button>
                  )
                )}
              </span>
              <button className="btn-lock" disabled={!onToggleLock} title={s.limite_mode === "locked" ? "Destravar" : "Cravar"}
                onClick={() => onToggleLock?.(meta.key)}>{s.limite_mode === "locked" ? "🔒" : "🔓"}</button>
            </div>
          );
        })}
      </section>

      <section className="supply-detail-sienge">
        <h3>Sienge</h3>
        {rows === null ? <Loader /> : !siengeDetail ? (
          <p className="sienge-empty">{canAssertSiengeAbsence
            ? "Nenhum documento do Sienge encontrado para este insumo (solicitação, pedido ou título). A etapa \"Mapa de concorrência\" não tem espelho no Sienge e segue manual."
            : "Vínculo Sienge não comprovado neste espelho. A ausência de documentos não é conclusiva até uma sincronização completa."}</p>
        ) : (
          <>
            {(item.sienge?.alerts ?? []).includes("financeiro_vencido") && (
              <p className="sienge-alert sienge-alert--overdue">
                Parcela vencida em aberto{siengeDetail.summary?.financial?.overdue?.oldest_due_date ? ` desde ${fmtDate(siengeDetail.summary.financial.overdue.oldest_due_date)}` : ""} — acionar o financeiro para não atrasar o recebimento.
              </p>
            )}
            {canAssertSiengeAbsence && (item.sienge?.alerts ?? []).includes("sem_fornecedor") && (
              <p className="sienge-alert sienge-alert--no-supplier">
                Sem OC autorizada e o limite de contratação está próximo — fornecedor ainda não fechado.
              </p>
            )}
            {siengeDetail.summary && (
              <p className="sienge-summary">
                Comprometido em OCs autorizadas: <b>{fmtBRL(siengeDetail.summary.committed_total)}</b>
                {item.sienge?.financial?.committed_pct != null && <> ({item.sienge.financial.committed_pct}% do orçado)</>}
                {siengeDetail.summary.financial && (
                  <> · Pago: <b>{fmtBRL(siengeDetail.summary.financial.paid_amount)}</b>{item.sienge?.financial?.paid_pct != null && <> ({item.sienge.financial.paid_pct}% dos títulos)</>}</>
                )}
              </p>
            )}
            {siengeDetail.requests?.length > 0 && (
              <div className="sienge-block">
                <h4>Solicitações de compra</h4>
                <table>
                  <thead><tr><th>Solicitação</th><th>Data</th><th>Qtd.</th><th>Situação</th></tr></thead>
                  <tbody>
                    {siengeDetail.requests.map((r) => (
                      <tr key={`${r.purchase_request_id}-${r.item_number}`}>
                        <td>#{r.purchase_request_id}</td>
                        <td>{fmtDate(r.request_date)}</td>
                        <td>{r.quantity ?? "—"} {r.unity_symbol ?? ""}</td>
                        <td>{r.disapproved ? "reprovada" : r.authorized ? "autorizada" : "aguardando"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {siengeDetail.orders?.length > 0 && (
              <div className="sienge-block">
                <h4>Pedidos de compra</h4>
                <table>
                  <thead><tr><th>OC</th><th>Fornecedor</th><th>Data</th><th>Valor (insumo)</th><th>Situação</th></tr></thead>
                  <tbody>
                    {siengeDetail.orders.map((o) => (
                      <tr key={o.purchase_order_id}>
                        <td>{o.formatted_id}</td>
                        <td>{o.supplier_name ?? "—"}</td>
                        <td>{fmtDate(o.date)}</td>
                        <td>{fmtBRL(o.resource_total)}</td>
                        <td>
                          {o.disapproved ? "reprovado" : !o.authorized ? "aguardando autorização"
                            : o.status === "FULLY_DELIVERED" ? "entregue"
                            : o.status === "PARTIALLY_DELIVERED" ? "parcialmente entregue"
                            : o.status === "CANCELED" ? "cancelado" : "autorizado"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {siengeDetail.installments?.length > 0 && (
              <div className="sienge-block">
                <h4>Parcelas dos títulos</h4>
                <table>
                  <thead><tr><th>Título</th><th>Credor</th><th>Vencimento</th><th>Valor</th><th>Situação</th></tr></thead>
                  <tbody>
                    {siengeDetail.installments.map((p) => {
                      const open = (p.balance_amount ?? 0) > 0;
                      const overdue = open && p.due_date && new Date(p.due_date) < today;
                      return (
                        <tr key={`${p.bill_id}-${p.installment_id}`} className={overdue ? "installment-overdue" : ""}>
                          <td>{p.bill_id}</td>
                          <td title={p.creditor_name}>{p.creditor_name ?? "—"}</td>
                          <td>{fmtDate(p.due_date)}</td>
                          <td>{fmtBRL(p.amount)}</td>
                          <td>{!open ? "paga" : overdue ? "vencida" : (p.paid_amount ?? 0) > 0 ? "parcial" : "em aberto"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section className="supply-detail-breakdown">
        <h3>Onde é usado</h3>
        {rows === null ? <Loader /> : rows.length === 0 ? <p>Sem quebra disponível.</p> : (
          <table>
            <thead><tr><th>Serviço</th><th>Qtd.</th><th>Valor</th><th>Planejado</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.wbs_code}>
                  <td title={r.wbs_code}>{r.label}</td>
                  <td>{r.quantity ?? "—"}</td>
                  <td>{fmtBRL(r.total_value)}</td>
                  <td>{fmtDate(r.planned_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default SupplyItemDetailModal;
